import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import AgeVerification from '../components/AgeVerification';
import CookieConsent from '../components/CookieConsent';
import { BookOpen, Target, DollarSign, Shield, CreditCard, Smartphone, Users, TrendingUp, Award } from 'lucide-react';

const Guides = () => {
  const guides = [
    {
      id: 'choosing-casino',
      title: 'Choosing the Right Casino',
      description: 'Master the art of selecting licensed, trustworthy online casinos that match your gaming preferences and ensure fair play.',
      icon: <Target className="h-8 w-8" />,
      color: 'from-blue-500 to-blue-700'
    },
    {
      id: 'bonuses-promotions',
      title: 'Understanding Bonuses & Promotions',
      description: 'Decode the world of casino bonuses, from welcome offers to loyalty rewards, and learn how to maximize their value.',
      icon: <DollarSign className="h-8 w-8" />,
      color: 'from-green-500 to-green-700'
    },
    {
      id: 'bankroll-management',
      title: 'Bankroll Management',
      description: 'Develop professional strategies for managing your gaming budget and extending your playing sessions.',
      icon: <TrendingUp className="h-8 w-8" />,
      color: 'from-purple-500 to-purple-700'
    },
    {
      id: 'game-selection',
      title: 'Game Selection Strategies',
      description: 'Discover which casino games offer the best odds and entertainment value for different player types.',
      icon: <Award className="h-8 w-8" />,
      color: 'from-red-500 to-red-700'
    },
    {
      id: 'responsible-gaming',
      title: 'Responsible Gaming Practices',
      description: 'Essential guidelines for maintaining control and ensuring gaming remains an enjoyable, sustainable activity.',
      icon: <Shield className="h-8 w-8" />,
      color: 'from-teal-500 to-teal-700'
    },
    {
      id: 'payment-methods',
      title: 'Payment Methods & Security',
      description: 'Navigate secure deposit and withdrawal options available to UK players with confidence and peace of mind.',
      icon: <CreditCard className="h-8 w-8" />,
      color: 'from-orange-500 to-orange-700'
    },
    {
      id: 'mobile-gaming',
      title: 'Mobile Gaming Excellence',
      description: 'Unlock the full potential of mobile casino gaming with tips for optimal performance and seamless experiences.',
      icon: <Smartphone className="h-8 w-8" />,
      color: 'from-indigo-500 to-indigo-700'
    },
    {
      id: 'live-dealer',
      title: 'Live Dealer Gaming Guide',
      description: 'Immerse yourself in authentic casino atmosphere with strategies for live dealer games and social interaction.',
      icon: <Users className="h-8 w-8" />,
      color: 'from-pink-500 to-pink-700'
    },
    {
      id: 'maximizing-winnings',
      title: 'Maximizing Your Winnings',
      description: 'Advanced techniques for optimizing your gameplay and turning entertainment into profitable opportunities.',
      icon: <BookOpen className="h-8 w-8" />,
      color: 'from-yellow-500 to-yellow-700'
    }
  ];

  return (
    <div className="min-h-screen bg-book-cream">
      <Header />
      <main className="book-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-5xl font-bold text-book-brown mb-6">
              How to Play Online Casinos in the UK
            </h1>
            <p className="text-xl text-book-brown-dark max-w-4xl mx-auto leading-relaxed">
              Embark on your journey to casino mastery with our comprehensive collection of expert guides.
              Each tip is crafted from years of industry experience, designed to transform casual players into confident, strategic gamers
              who understand the nuances of online casino entertainment in the UK market.
            </p>
          </div>

          <div className="mb-16 bg-book-beige rounded-xl p-8 shadow-book-inset">
            <h2 className="text-2xl font-bold text-book-brown mb-6 text-center">Our Guide Writing Philosophy</h2>
            <div className="prose prose-lg max-w-none text-book-brown-dark leading-relaxed">
              <p className="mb-4">
                Every guide published on Brit Ember represents the culmination of extensive research, real-world testing,
                and deep industry insights accumulated over decades of casino expertise. We don't just compile information –
                we analyze, test, and validate every recommendation through rigorous methodology that ensures accuracy and relevance.
              </p>
              <p className="mb-4">
                Our writing process begins with comprehensive market analysis, examining hundreds of casino platforms,
                gaming software providers, and regulatory frameworks. Each tip undergoes multiple rounds of peer review,
                statistical validation, and practical testing to ensure it delivers genuine value to players at every skill level.
              </p>
              <p className="mb-4">
                What sets our guides apart is the integration of psychological insights, mathematical probability analysis,
                and behavioral economics principles. We understand that successful casino gaming isn't just about luck –
                it's about informed decision-making, disciplined execution, and strategic thinking that maximizes both
                entertainment value and long-term sustainability.
              </p>
              <p className="mb-4">
                Every recommendation is backed by empirical data, statistical analysis, and real-world performance metrics.
                We maintain transparency in our methodologies, clearly explaining the reasoning behind each tip while
                acknowledging the inherent uncertainties of casino gaming. Our goal isn't to promise guaranteed wins,
                but to empower players with knowledge that leads to more informed, enjoyable, and responsible gaming experiences.
              </p>
              <p>
                As the casino landscape evolves, so do our guides. We continuously monitor industry developments,
                regulatory changes, and emerging trends to ensure our advice remains current, relevant, and effective.
                This commitment to ongoing research and validation means you can trust our guides as authoritative,
                practical resources for navigating the complex world of online casinos in the UK.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {guides.map((guide, index) => (
              <Link
                key={guide.id}
                to={`/guides/${guide.id}`}
                data-discover="true"
                className="group bg-white rounded-xl shadow-book hover:shadow-lg transition-all duration-200 overflow-hidden"
              >
                <div className={`bg-gradient-to-br ${guide.color} p-6 text-white`}>
                  <div className="flex items-center justify-center mb-4">
                    {guide.icon}
                  </div>
                  <h3 className="text-xl font-bold text-center mb-2">{guide.title}</h3>
                </div>
                <div className="p-6">
                  <p className="text-book-brown-dark mb-4 leading-relaxed">
                    {guide.description}
                  </p>
                  <div className="flex items-center text-book-brown-light group-hover:text-book-brown transition-colors duration-200">
                    <span className="font-medium">Read Full Guide</span>
                    <svg className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform duration-200" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          <div className="mt-16 text-center">
            <div className="bg-gradient-to-r from-book-brown to-book-brown-light rounded-xl p-8 text-book-cream">
              <h3 className="text-book-cream text-2xl font-bold mb-4">Ready to Elevate Your Gaming?</h3>
              <p className="text-lg opacity-90 mb-6 max-w-2xl mx-auto">
                These comprehensive guides represent the foundation of strategic casino gaming.
                Whether you're just starting out or looking to refine your approach, our expert insights
                will transform how you experience online casinos in the UK.
              </p>
              <p className="text-sm opacity-80">
                All guides are regularly updated to reflect the latest industry developments and best practices.
              </p>
            </div>
          </div>
        </div>
      </main>
      <Footer />
      <AgeVerification />
      <CookieConsent />
    </div>
  );
};

export default Guides;
